/* eslint-disable @woocommerce/dependency-group */
/* global jQuery */
import { __ } from '@wordpress/i18n';
import { useState, useEffect, createElement } from '@wordpress/element';
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { decodeEntities } from '@wordpress/html-entities';
import { getSetting } from '@woocommerce/settings';
import { cartStore } from '@woocommerce/block-data';
import { useSelect } from '@wordpress/data';

const gatewaySlug = 'finix_gateway';

const gatewaySettings = getSetting( 'finix_gateway_data', {} );

const label = decodeEntities( gatewaySettings.title ) || __( 'Pay with Card', 'finix-for-woocommerce' );

const nonEmptyValue = ( value ) => value && value.trim() !== '';

/**
 * Label component.
 *
 * @param {*} props Props from payment API.
 */
const Label = ( props ) => {
	const { PaymentMethodLabel } = props.components;

	const Icon = () => {
		return createElement( 'img', {
			src: gatewaySettings.icon,
		} );
	};

	return <PaymentMethodLabel text={ label } icon={ <Icon /> } />;
};

/**
 * Content component.
 *
 * @param {*} props Props from payment API.
 */
const Content = ( props ) => {
	const { activePaymentMethod, eventRegistration, emitResponse } = props;
	const { onPaymentSetup, onCheckoutFail } = eventRegistration;
	const [ form, setForm ] = useState( null );
	const [ fraudSessionId, setFraudSessionId ] = useState( '' );
	const [ token, setToken ] = useState( '' );
	const { totals } = useSelect( ( select ) => select( cartStore ).getCartData(), [] );
	const [ finixMerchantId, setFinixMerchantId ] = useState( '' );

	/*
	 * The currency may change when currency switching plugins are used on a site.
	 * The totals will be updated in this case as well, so we need to keep track of this
	 * and use the correct Merchant ID.
	 */
	useEffect( () => {
		setFinixMerchantId( totals.currency_code === 'CAD' ? gatewaySettings.merchant_cad : gatewaySettings.merchant );
	}, [ totals ] );

	// TODO: Define tokenBlocksRequest function outside the component.
	const tokenBlocksRequest = async () => {
		// Disable the place_order button to prevent multiple submissions.
		document.querySelector( 'button.wc-block-components-checkout-place-order-button' ).disabled = true;

		// Wrap the form submission in a promise.
		return new Promise( ( resolve, reject ) => {
			form.submit( function ( err, res ) {
				if ( err ) {
					document.querySelector( 'button.wc-block-components-checkout-place-order-button' ).disabled = false;
					// Reject the promise with the error.
					reject( err );
				} else {
					// Get tokenBlocks ID from response.
					const tokenDataBlocks = res.data || {};
					const tokenBlocks = tokenDataBlocks.id;
					// Resolve the promise with the tokenBlocks.
					resolve( tokenBlocks );
				}
			} );
		} );
	};

	// Clear the token when the checkout fails so that we can reinitialize the form.
	useEffect( () => {
		setToken( '' );
	}, [ onCheckoutFail ] );

	useEffect( () => {
		const referenceInput = jQuery( 'input#billing-city, input#shipping-city' );

		// Check if browser is in dark mode.
		const isDarkMode = window.matchMedia && window.matchMedia( '(prefers-color-scheme: dark)' ).matches;

		let copiedStyles = {
			color: referenceInput.css( 'color' ),
			backgroundColor: referenceInput.css( 'background-color' ),
			border: `${ referenceInput.css( 'border-width' ) } ${ referenceInput.css( 'border-style' ) } ${ referenceInput.css( 'border-color' ) }`,
			borderRadius: `${ referenceInput.css( 'border-top-left-radius' ) } ${ referenceInput.css( 'border-top-right-radius' ) } ${ referenceInput.css( 'border-bottom-right-radius' ) } ${ referenceInput.css( 'border-bottom-left-radius' ) }`,
			fontFamily: referenceInput.css( 'font-family' ),
			fontSize: referenceInput.css( 'font-size' ),
			fontWeight: referenceInput.css( 'font-weight' ),
			lineHeight: referenceInput.css( 'line-height' ),
			boxShadow: referenceInput.css( 'box-shadow' ),
			height: `${ referenceInput.css( 'height' ) }`,
			padding: `${ referenceInput.css( 'padding-bottom' ) } ${ referenceInput.css( 'padding-right' ) } ${ referenceInput.css( 'padding-bottom' ) } ${ referenceInput.css( 'padding-left' ) }`,
		};

		// Define options for the form.
		const options_blocks = {
			paymentMethods: [ 'card' ],
			showAddress: gatewaySettings.finix_form_options.showAddress ?? true,
			showLabels: gatewaySettings.finix_form_options.showLabels ?? true,
			labels: gatewaySettings.finix_form_options.labels || {
				card_holder_name: __( 'Full Name', 'finix-for-woocommerce' ),
			},
			showPlaceholders: gatewaySettings.finix_form_options.showPlaceholders ?? true,
			placeholders: gatewaySettings.finix_form_options.placeholders || {
				card_holder_name: __( 'Full Name', 'finix-for-woocommerce' ),
			},
			defaultValues: gatewaySettings.finix_form_options.defaultValues,
			hideFields: gatewaySettings.finix_form_options.hideFields || [ 'address_line1', 'address_line2', 'address_city', 'address_state' ],
			requiredFields: gatewaySettings.finix_form_options.requiredFields || [ 'card_holder_name', 'address_country', 'address_postal_code' ],
			hideErrorMessages: gatewaySettings.finix_form_options.hideErrorMessages ?? false,
			errorMessages: gatewaySettings.finix_form_options.errorMessages || {
				card_holder_name: __( 'Please enter a valid full name', 'finix-for-woocommerce' ),
			},
			enableDarkMode: gatewaySettings.finix_form_options.enableDarkMode ?? isDarkMode,
			theme: gatewaySettings.finix_form_options.theme || 'finix',
			requireSecurityCode: gatewaySettings.finix_form_options.requireSecurityCode ?? true,
			// Customize styles based on the reference input styles.
			styles: {
				default: {
					input: {
						default: copiedStyles,
						error: {},
						success: {},
						focused: {},
					},
				},
				dark: {
					input: {
						default: copiedStyles,
						error: {},
						success: {},
						focused: {},
					},
				},
			},
			// Callback function that will trigger when form state changes (can be called frequently).
			onUpdate( state, binInformation, formHasErrors ) {},
			// Optional callback function that will trigger after the form has loaded.
			onLoad() {},
		};

		// Get the Finix Fraud Session ID.
		window.Finix.Auth( gatewaySettings.environment, finixMerchantId, ( sessionKey ) => {
			setFraudSessionId( sessionKey );
		} );

		const identifier = gatewaySlug + '-form';

		// Define a recursive function to initialize Finix form.
		const recursiveInitFinixForm = ( counter, newForm = null ) => {
			if ( counter > 4 ) {
				document.getElementById( identifier ).innerHTML = '<p>' + __( 'Unfortunately, there was an error while loading the payment form. Please try again later.', 'finix-for-woocommerce' ) + '</p>';

				return;
			}

			if ( document.getElementById( identifier ).innerHTML === '' ) {
				newForm = window.Finix.PaymentForm( identifier, gatewaySettings.environment, gatewaySettings.application, options_blocks );

				setTimeout( () => {
					counter++;
					recursiveInitFinixForm( counter, newForm );
				}, 500 );
			} else {
				setForm( newForm );
			}
		};

		// Call the recursive function to initialize the form.
		recursiveInitFinixForm( 0 );

		// Cleanup function.
		return () => {};
	}, [ onPaymentSetup ] );

	// Inside FinixCardContent functional component.
	useEffect( () => {
		const validatePaymentSetup = onPaymentSetup( async () => {
			const selected_payment_method = document.querySelector( 'input[name="radio-control-wc-payment-method-options"]:checked' ).value;

			if ( selected_payment_method !== gatewaySlug ) {
				return;
			}

			if ( selected_payment_method !== activePaymentMethod ) {
				return;
			}

			// If we don't have a form - return an error.
			if ( ! form ) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __( 'Unfortunately, there was an error while initializing a payment form.', 'finix-for-woocommerce' ),
				};
			}

			let paymentMethodData = {
				finix_fraud_session_id: fraudSessionId,
				finix_nonce: gatewaySettings.nonce,
			};

			// If we don't have a token - request one.
			if ( ! token || token === '' ) {
				try {
					// Wait until the tokenBlocks request finishes.
					const responseToken = await tokenBlocksRequest( form );
					setToken( responseToken );

					paymentMethodData[ gatewaySlug + '_token' ] = responseToken;

					return {
						type: emitResponse.responseTypes.SUCCESS,
						meta: {
							paymentMethodData: paymentMethodData,
						},
					};
				} catch ( error ) {
					setToken( '' );
					document.querySelector( 'button.wc-block-components-checkout-place-order-button' ).disabled = false;

					return {
						type: emitResponse.responseTypes.ERROR,
						message: error,
					};
				}
			}

			paymentMethodData[ gatewaySlug + '_token' ] = token;

			// If we have a form and a token - proceed further by emitting success.
			return {
				type: emitResponse.responseTypes.SUCCESS,
				meta: {
					paymentMethodData: paymentMethodData,
				},
			};
		} );

		// Cleanup function.
		return () => validatePaymentSetup();
	}, [ onPaymentSetup, form, emitResponse.responseTypes.SUCCESS, emitResponse.responseTypes.ERROR, fraudSessionId ] );

	return createElement(
		'fieldset',
		{
			id: 'wc-' + gatewaySlug + '-form',
			className: 'finix-wc-form wc-payment-form',
		},
		// If gatewaySettings.description is not empty, create div > p to display.
		nonEmptyValue( gatewaySettings.description )
			? createElement(
					'div',
					{},
					createElement( 'p', {
						dangerouslySetInnerHTML: {
							__html: gatewaySettings.description,
						},
					} )
			  )
			: null,
		createElement( 'div', { id: gatewaySlug + '-form' } )
	);
};

const Edit = () => {};

/**
 * Finix payment method config object.
 *
 * @see https://developer.woocommerce.com/docs/cart-and-checkout-payment-method-integration-for-the-checkout-block/#registration
 */
registerPaymentMethod( {
	name: gatewaySlug,
	label: <Label />,
	content: <Content />,
	edit: <Edit />,
	canMakePayment: () => true,
	ariaLabel: label,
	supports: {
		features: gatewaySettings.supports,
	},
} );
